// Chip / Badge — compact status or category label. Intent maps to a semantic
// token pair; `dot` adds a leading status dot, `iconStart` a leading glyph.

import { forwardRef, type HTMLAttributes } from 'react';
import Icon from './Icon';

export type ChipVariant =
  | 'brand'
  | 'brand-solid'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'purple'
  | 'neutral'
  | 'outline';

export type ChipSize = 'md' | 'lg';

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: ChipVariant;
  /** Chip height/type scale: 'md' (default) or the roomier 'lg'. */
  size?: ChipSize;
  /** Show a leading status dot in the current text color. */
  dot?: boolean;
  /** Kebab-case Icon name rendered before the label. */
  iconStart?: string;
}

const Chip = forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  { variant = 'neutral', size = 'md', dot = false, iconStart, className = '', children, ...rest },
  ref,
) {
  const classes = [
    'app-chip',
    `app-chip--${variant}`,
    size === 'lg' && 'app-chip--lg',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span ref={ref} className={classes} {...rest}>
      {dot && <span className='app-chip__dot' aria-hidden='true' />}
      {iconStart && (
        <Icon
          name={iconStart}
          size={size === 'lg' ? 15 : 13}
          className='app-chip__icon'
          aria-hidden='true'
        />
      )}
      {children}
    </span>
  );
});

export default Chip;
